import clsx from 'clsx';

import { IconArrowUp, IconArrowDown } from '@tabler/icons-react';

import { TTrendDirection } from '@/types';

import SkeletonLoader from '../SkeletonLoader';

interface ISummaryCardProps {
  label: string;
  value: React.ReactNode;
  icon?: React.ReactNode;

  /** Variant */
  variant: 'highlight' | 'dashboard';

  /** Highlight card props */
  bg?: string;
  text?: string;
  sub?: string;

  /** Dashboard card props */
  percentage?: number;
  trend?: number | string;
  trendDirection?: TTrendDirection | string;
  isPositive?: boolean;
  trendLabel?: string;

  className?: string;
  isLoading: boolean;
}

export const SummaryCard: React.FC<ISummaryCardProps> = ({
  label,
  value,
  icon,
  variant,
  bg = '',
  text = '',
  sub = '',
  trend,
  trendDirection,
  trendLabel,
  className = '',
  isLoading = false,
}) => {
  /* HIGHLIGHT VARIANT */
  if (variant === 'highlight') {
    return (
      <div
        className={clsx(
          'py-2.5 px-4 rounded-lg flex flex-col justify-center items-center',
          bg,
          className
        )}
      >
        <div className={clsx('text-xs font-semibold leading-5', sub)}>
          {label}
        </div>

        <div className="flex items-center justify-center mt-1 gap-1">
          {icon && (
            <div className="flex items-center justify-center">{icon}</div>
          )}
          <div className={clsx('text-2xl font-semibold', text)}>{value}</div>
        </div>
      </div>
    );
  }

  /* DASHBOARD VARIANT */
  return !isLoading ? (
    <div
      className={clsx(
        'flex flex-row items-center justify-between rounded-xl border border-neutral-light-200 bg-card shadow-2xs p-4 gap-2.5',
        className
      )}
    >
      <span className="p-2 rounded-lg border border-neutral-light-200 shadow-2xs">
        {icon}
      </span>

      <div className="flex flex-row justify-between space-y-1.5 w-full gap-4">
        <div className="flex justify-center flex-col items-start mb-0 w-full">
          <p className="text-sm leading-5 font-semibold text-neutral-primary">
            {label}
          </p>
          <div className="flex flex-row justify-between space-y-1.5 w-full gap-4">
            <span className="text-2xl leading-8 font-semibold text-neutral-primary">
              {value}
            </span>
            {(trend || trendDirection || trendLabel) && (
              <div className="flex justify-center items-end gap-1.5">
                <span className="flex flex-col lg:flex-row justify-end items-center gap-1.5">
                  {(trend || trendDirection) && (
                    <span
                      className={clsx(
                        'inline-flex items-center rounded-full px-2 py-1 text-sm font-medium gap-1',
                        trendDirection === 'UP'
                          ? 'bg-success-light-50 text-success-default'
                          : trendDirection === 'DOWN'
                            ? 'bg-error-light-50 text-error-default'
                            : 'bg-neutral-light-100 text-neutral-secondary'
                      )}
                    >
                      {trendDirection === 'UP' && <IconArrowUp size={16} />}
                      {trendDirection === 'DOWN' && <IconArrowDown size={16} />}
                      {trend}
                    </span>
                  )}
                  {trendLabel && (
                    <p className="text-sm text-neutral-secondary">
                      {trendLabel}
                    </p>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <div
      className={clsx(
        'flex flex-row items-center justify-between rounded-xl border border-neutral-light-200 bg-card shadow-2xs p-4 gap-2.5 h-23',
        className
      )}
    >
      <SkeletonLoader className="h-10 w-10.5" variant="text" />
      <div className="flex flex-col justify-center items-start w-full gap-2">
        <SkeletonLoader className="h-5 w-42" variant="text" />
        <div className="flex justify-between gap-2 w-full">
          <SkeletonLoader className="h-6 w-6" variant="text" />
          <SkeletonLoader className="h-6 w-20" variant="text" />
        </div>
      </div>
    </div>
  );
};
